/**
 * Demo Genie Brand Registry
 *
 * Maps each subcategory to a fictional brand identity used for
 * demo genie logos, branding, and the discovery flow.
 * One brand per subcategory — all genies in a subcategory share the same brand.
 */

export interface SubcategoryBrand {
  /** The subcategory this brand belongs to */
  subcategoryId: string;
  /** Industry grouping */
  industryId: string;
  /** Fictional brand / company name */
  brandName: string;
  /** Short tagline shown beneath the logo */
  tagline: string;
  /** Primary brand color (hex) */
  primaryColor: string;
  /** Secondary / accent color (hex) */
  secondaryColor: string;
  /** Suggested icon keywords for logo generation */
  iconHints: string;
}

export const SUBCATEGORY_BRANDS: SubcategoryBrand[] = [
  // =========================================================================
  // Automotive
  // =========================================================================
  {
    subcategoryId: 'auto-dealers',
    industryId: 'automotive',
    brandName: 'DriveFree',
    tagline: 'Your Journey Starts Here',
    primaryColor: '#1B5E8C',
    secondaryColor: '#3BA4D9',
    iconHints: 'sleek car silhouette, speed lines, road',
  },
  {
    subcategoryId: 'auto-repair',
    industryId: 'automotive',
    brandName: 'AutoCare Pro',
    tagline: 'Precision Service',
    primaryColor: '#2D3E50',
    secondaryColor: '#E67E22',
    iconHints: 'wrench, gear, engine, tools',
  },
  {
    subcategoryId: 'tire-shops',
    industryId: 'automotive',
    brandName: 'TreadMaster',
    tagline: 'Road Ready',
    primaryColor: '#333333',
    secondaryColor: '#C0392B',
    iconHints: 'tire, wheel, tread pattern, road',
  },

  // =========================================================================
  // Marine
  // =========================================================================
  {
    subcategoryId: 'boat-dealers',
    industryId: 'marine',
    brandName: 'Oceanus Marine',
    tagline: 'Navigate with Confidence',
    primaryColor: '#1A3A5C',
    secondaryColor: '#2A9D8F',
    iconHints: 'anchor in shield, nautical crest, helm wheel',
  },
  {
    subcategoryId: 'marinas',
    industryId: 'marine',
    brandName: 'Harbor Point',
    tagline: 'Your Home on the Water',
    primaryColor: '#0C4A6E',
    secondaryColor: '#38BDF8',
    iconHints: 'lighthouse, dock, sailboat in harbor',
  },
  {
    subcategoryId: 'marine-services',
    industryId: 'marine',
    brandName: 'AquaTech Marine',
    tagline: 'Marine Excellence',
    primaryColor: '#164E63',
    secondaryColor: '#06B6D4',
    iconHints: 'propeller, boat hull, wave and wrench',
  },

  // =========================================================================
  // Appliances
  // =========================================================================
  {
    subcategoryId: 'appliance-repair',
    industryId: 'appliances',
    brandName: 'FixRight',
    tagline: 'Expert Repair Solutions',
    primaryColor: '#1E3A5F',
    secondaryColor: '#E76F51',
    iconHints: 'wrench with appliance, repair tools, checkmark',
  },
  {
    subcategoryId: 'appliance-retail',
    industryId: 'appliances',
    brandName: 'Aether Appliances',
    tagline: 'Smart Living Solutions',
    primaryColor: '#2A9D8F',
    secondaryColor: '#6B7280',
    iconHints: 'stylized letter A with swoosh, modern appliance silhouette',
  },
  {
    subcategoryId: 'electronics-retail',
    industryId: 'appliances',
    brandName: 'NovaTech',
    tagline: 'Smart Living Solutions',
    primaryColor: '#374151',
    secondaryColor: '#2DD4BF',
    iconHints: 'power button icon, circuit, tech swoosh',
  },

  // =========================================================================
  // Office Equipment
  // =========================================================================
  {
    subcategoryId: 'copier-dealers',
    industryId: 'office-equipment',
    brandName: 'OfficePro',
    tagline: 'Efficiency Engineered',
    primaryColor: '#1B6B5C',
    secondaryColor: '#3B82F6',
    iconHints: 'printer/copier icon, office building, document',
  },
  {
    subcategoryId: 'managed-print',
    industryId: 'office-equipment',
    brandName: 'PrintFlow',
    tagline: 'Managed Print Solutions',
    primaryColor: '#4338CA',
    secondaryColor: '#818CF8',
    iconHints: 'flowing paper, print layers, document stack',
  },
  {
    subcategoryId: 'office-tech',
    industryId: 'office-equipment',
    brandName: 'TechEdge',
    tagline: 'Office Innovation',
    primaryColor: '#0F766E',
    secondaryColor: '#2DD4BF',
    iconHints: 'monitor with checkmark, tech grid, digital workspace',
  },

  // =========================================================================
  // Home Builders
  // =========================================================================
  {
    subcategoryId: 'general-contractor',
    industryId: 'home-builders',
    brandName: 'Summit Builders',
    tagline: 'Building Excellence',
    primaryColor: '#1E3A5F',
    secondaryColor: '#E76F51',
    iconHints: 'house with hammer, construction, roofline with tools',
  },
  {
    subcategoryId: 'flooring',
    industryId: 'home-builders',
    brandName: 'Foundation Floors',
    tagline: 'Step into Quality',
    primaryColor: '#78350F',
    secondaryColor: '#D97706',
    iconHints: 'floor tile pattern, hardwood plank, house foundation',
  },
  {
    subcategoryId: 'kitchen-bath',
    industryId: 'home-builders',
    brandName: 'CraftSpace',
    tagline: 'Kitchen & Bath Specialists',
    primaryColor: '#4A5568',
    secondaryColor: '#2A9D8F',
    iconHints: 'kitchen counter, faucet, tile mosaic',
  },
  {
    subcategoryId: 'roofing',
    industryId: 'home-builders',
    brandName: 'SkyShield',
    tagline: 'Protection from Above',
    primaryColor: '#1E293B',
    secondaryColor: '#3B82F6',
    iconHints: 'roofline with shield, house roof, protection',
  },

  // =========================================================================
  // Travel & Hospitality
  // =========================================================================
  {
    subcategoryId: 'hotels',
    industryId: 'travel-hospitality',
    brandName: 'Horizon Stays',
    tagline: 'Your Journey Begins',
    primaryColor: '#1E3A5F',
    secondaryColor: '#F59E0B',
    iconHints: 'sunset over mountains, bed with horizon, accommodation',
  },
  {
    subcategoryId: 'tours-activities',
    industryId: 'travel-hospitality',
    brandName: 'Wanderlux',
    tagline: 'Discover More',
    primaryColor: '#7C3AED',
    secondaryColor: '#F59E0B',
    iconHints: 'compass rose, hiking trail, adventure binoculars',
  },
  {
    subcategoryId: 'travel-agency',
    industryId: 'travel-hospitality',
    brandName: 'Compass Travel',
    tagline: 'Navigate Your Next Adventure',
    primaryColor: '#0C4A6E',
    secondaryColor: '#F97316',
    iconHints: 'compass, globe, airplane, world map',
  },

  // =========================================================================
  // Manufacturing
  // =========================================================================
  {
    subcategoryId: 'general-manufacturing',
    industryId: 'manufacturing',
    brandName: 'ForgeWorks',
    tagline: 'Industrial Precision',
    primaryColor: '#374151',
    secondaryColor: '#EF4444',
    iconHints: 'factory gear, anvil, industrial machinery',
  },
  {
    subcategoryId: 'electronics-manufacturing',
    industryId: 'manufacturing',
    brandName: 'CircuitCore',
    tagline: 'Precision Manufacturing',
    primaryColor: '#1E3A5F',
    secondaryColor: '#10B981',
    iconHints: 'circuit board, microchip, electronic components',
  },
  {
    subcategoryId: 'food-beverage',
    industryId: 'manufacturing',
    brandName: 'HarvestLine',
    tagline: 'From Source to Scale',
    primaryColor: '#166534',
    secondaryColor: '#F59E0B',
    iconHints: 'grain wheat, production line, bottle and leaf',
  },

  // =========================================================================
  // Industrial
  // =========================================================================
  {
    subcategoryId: 'industrial-equipment',
    industryId: 'industrial',
    brandName: 'IronPoint',
    tagline: 'Heavy Duty Solutions',
    primaryColor: '#1F2937',
    secondaryColor: '#DC2626',
    iconHints: 'heavy machinery, crane, industrial equipment',
  },
  {
    subcategoryId: 'logistics',
    industryId: 'industrial',
    brandName: 'SwiftRoute',
    tagline: 'Seamless Logistics',
    primaryColor: '#1E3A5F',
    secondaryColor: '#3B82F6',
    iconHints: 'truck, route arrows, shipping container',
  },
  {
    subcategoryId: 'warehousing',
    industryId: 'industrial',
    brandName: 'VaultStore',
    tagline: 'Storage Solutions',
    primaryColor: '#374151',
    secondaryColor: '#8B5CF6',
    iconHints: 'warehouse building, storage shelves, boxes',
  },

  // =========================================================================
  // Trades
  // =========================================================================
  {
    subcategoryId: 'electrical',
    industryId: 'trades',
    brandName: 'BrightWire',
    tagline: 'Powering Your World',
    primaryColor: '#1E3A5F',
    secondaryColor: '#FBBF24',
    iconHints: 'lightning bolt, electrical plug, power lines',
  },
  {
    subcategoryId: 'hvac',
    industryId: 'trades',
    brandName: 'ClimateCore',
    tagline: 'Comfort Engineered',
    primaryColor: '#0369A1',
    secondaryColor: '#EF4444',
    iconHints: 'snowflake and flame, thermostat, air flow',
  },
  {
    subcategoryId: 'landscaping',
    industryId: 'trades',
    brandName: 'GreenScape',
    tagline: 'Nature by Design',
    primaryColor: '#166534',
    secondaryColor: '#84CC16',
    iconHints: 'leaf, tree, garden landscape',
  },
  {
    subcategoryId: 'locksmith',
    industryId: 'trades',
    brandName: 'KeyMaster',
    tagline: 'Security Solutions',
    primaryColor: '#1F2937',
    secondaryColor: '#D97706',
    iconHints: 'key, lock, shield with keyhole',
  },
  {
    subcategoryId: 'painting',
    industryId: 'trades',
    brandName: 'TrueCoat',
    tagline: 'Color Your World',
    primaryColor: '#4338CA',
    secondaryColor: '#F59E0B',
    iconHints: 'paint roller, paint brush, color swatches',
  },
  {
    subcategoryId: 'plumbing',
    industryId: 'trades',
    brandName: 'FlowRight',
    tagline: 'Reliable Plumbing',
    primaryColor: '#0C4A6E',
    secondaryColor: '#38BDF8',
    iconHints: 'pipe wrench, water drop, plumbing pipes',
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the brand for a subcategory
 */
export const getBrandForSubcategory = (subcategoryId: string): SubcategoryBrand | undefined => {
  return SUBCATEGORY_BRANDS.find(b => b.subcategoryId === subcategoryId);
};

/**
 * Get all brands for an industry
 */
export const getBrandsForIndustry = (industryId: string): SubcategoryBrand[] => {
  return SUBCATEGORY_BRANDS.filter(b => b.industryId === industryId);
};

/**
 * Get all unique subcategory IDs
 */
export const getAllSubcategoryIds = (): string[] => {
  return SUBCATEGORY_BRANDS.map(b => b.subcategoryId);
};
